<!-- Ported from the Polaris kit: src/product/components/ListToolbar.vue.
     Classes translated to Tailwind 3 by rebranding_frontend/port-kit-component.mjs;
     strings moved to i18n by hand. Change the design in the kit first.
     Hand changes (phase P, 28/09):
     - The porter's rule for `[a,b]:x` arbitrary variants rewrote the JS computed key in `choose`
       (`{ [id]: value }` came out as `[&:is(id)]`); restored.
     - A filter option can be a string (the kit) or `{ value, label }`, so a stored value ("open") shows its words
       ("Open"/"Aberta"); the pill says the chosen option's label.
     - `noun` (['item', 'items']) becomes `countKey`: an i18n key with the plural, `{count}` inside
       (default POLARIS.LIST_TOOLBAR.COUNT). "is" and "Any" go through i18n. -->
<script setup>
// What sits above a list: search, filters as pills that say their value ("Status is Any"), how many rows are
// showing, and one quiet action such as Export. It only holds the choices; the list does the filtering.
// Slots `lead`, `beside-search` and `extra`: a control of the page that belongs in the same row, before the search
// (who we test as), right after it (a content type menu, when the reference puts it there) or after the filters (a
// sort menu, a date range).
import { useI18n } from 'vue-i18n';
import { ChevronDown16, Download16, Search16 } from '@carbon/icons-vue';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '../ui/input-group';

defineProps({
  placeholder: { type: String, default: '' }, // also the accessible name of the search field; empty hides the field
  filters: { type: Array, default: () => [] }, // [{ id, label, options: [String | { value, label }] }]
  count: { type: Number, default: null },
  countKey: { type: String, default: 'POLARIS.LIST_TOOLBAR.COUNT' }, // "{count} item | {count} items"
  exportLabel: { type: String, default: '' },
});
defineEmits(['export']);
const search = defineModel('search', { type: String, default: '' });
const chosen = defineModel('chosen', { type: Object, default: () => ({}) }); // { [filter.id]: value }; '' or missing means Any (0 is a value)
const { t } = useI18n();

const optionsOf = filter =>
  filter.options.map(option =>
    option !== null && typeof option === 'object'
      ? option
      : { value: option, label: String(option) }
  );
const chosenLabel = filter =>
  optionsOf(filter).find(option => option.value === chosen.value[filter.id])
    ?.label || t('POLARIS.LIST_TOOLBAR.ANY');
const choose = (id, value) => {
  chosen.value = { ...chosen.value, [id]: value };
};
</script>

<template>
  <div class="grid gap-3" data-list-toolbar>
    <div
      v-if="
        placeholder ||
        filters.length ||
        $slots.extra ||
        $slots.lead ||
        $slots['beside-search']
      "
      class="flex flex-wrap items-center gap-2"
    >
      <slot name="lead" />
      <InputGroup v-if="placeholder" class="w-full sm:w-52">
        <InputGroupInput
          v-model="search"
          type="search"
          :aria-label="placeholder"
          :placeholder="placeholder"
        />
        <InputGroupAddon><Search16 aria-hidden="true" /></InputGroupAddon>
      </InputGroup>
      <slot name="beside-search" />
      <DropdownMenu v-for="filter in filters" :key="filter.id">
        <DropdownMenuTrigger as-child>
          <Button variant="outline" size="sm">
            {{ t('POLARIS.LIST_TOOLBAR.FILTER_IS', { label: filter.label }) }}
            <span class="font-normal text-polaris-muted-foreground">
              {{ chosenLabel(filter) }}
            </span>
            <ChevronDown16 aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuRadioGroup
            :model-value="chosen[filter.id] ?? ''"
            @update:model-value="value => choose(filter.id, value)"
          >
            <DropdownMenuRadioItem value="">
              {{ t('POLARIS.LIST_TOOLBAR.ANY') }}
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem
              v-for="option in optionsOf(filter)"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <slot name="extra" />
    </div>
    <div
      v-if="count !== null || exportLabel"
      class="flex flex-wrap items-center gap-3"
    >
      <p
        v-if="count !== null"
        class="type-polaris-section-title"
        aria-live="polite"
      >
        {{ t(countKey, { count }, count) }}
      </p>
      <Button
        v-if="exportLabel"
        variant="secondary"
        size="sm"
        @click="$emit('export')"
      >
        <Download16 aria-hidden="true" />{{ exportLabel }}
      </Button>
    </div>
  </div>
</template>
